import React, { useState, useEffect, useMemo } from 'react';
import {
  FolderOpen, Folder, ChevronRight, ChevronDown,
  Calendar, Package, User, Building2, FileText,
  Clock, CheckCircle, XCircle, Eye, X,
  Hash, AlertCircle, Search, Download,
  RefreshCw, Upload, RotateCcw, Layers, Users, Sun, Moon
} from 'lucide-react';
import API_BASE_URL from "../config";
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import megalogo from '../Image/megalogo.png';

const DEPT1 = ['Mechanical', 'QC', 'Accounting', 'Logistics', 'Spareparts', 'Gen. Services', 'Production'];
const DEPT2 = ['RM Whse', 'Packaging', 'Electrical', 'FG Whse', 'Motorpool', 'Admin/HR/MIS', 'Fabrication'];

const STATUS_MAP = {
  '-1': { label: 'Rejected', bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', Icon: XCircle },
  '0': { label: 'Draft', bg: 'bg-slate-50', text: 'text-slate-500', border: 'border-slate-200', Icon: FileText },
  '1': { label: 'Pending', bg: 'bg-amber-50', text: 'text-slate-500', border: 'border-amber-200', Icon: Clock },
  '2': { label: 'Approved', bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', Icon: CheckCircle },
  '3': { label: 'Issued', bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', Icon: CheckCircle },
  '4': { label: 'Cancelled', bg: 'bg-slate-50', text: 'text-slate-400', border: 'border-slate-200', Icon: XCircle },
};

// ── Shift helpers ──────────────────────────────────────────────────────────────
// AM shift: 06:00 – 17:59  |  PM shift: 18:00 – 05:59 (next day)
// ✅ Pick the best available timestamp from an MR record
const getMRTimestamp = (mr) => {
  // Priority: requestedDate > mrDate > createdDate > updatedDate
  return mr?.requestedDate || mr?.mrDate || mr?.createdDate || mr?.updatedDate || null;
};

const getShift = (dateStr) => {
  if (!dateStr) return { key: 'unknown', label: 'Unknown', shift: 'AM', dateKey: 'unknown' };
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return { key: 'unknown', label: 'Unknown', shift: 'AM', dateKey: 'unknown' };
  const hour = d.getHours();
  // AM = 6:00 to 17:59  |  PM = 18:00 to 5:59 (next calendar day)
  const isAM = hour >= 6 && hour < 18;
  const dateKey = d.toISOString().split('T')[0];
  // Hours 0–5 belong to the PM shift of the PREVIOUS calendar day
  let shiftDate = dateKey;
  if (hour < 6) {
    const prev = new Date(d);
    prev.setDate(prev.getDate() - 1);
    shiftDate = prev.toISOString().split('T')[0];
  }
  const shift = isAM ? 'AM' : 'PM';
  return {
    key: `${shiftDate}_${shift}`,
    label: shift === 'AM' ? 'AM Shift (6:00–18:00)' : 'PM Shift (18:00–6:00)',
    shift,
    dateKey: shiftDate,
  };
};

export default function MRFolderByDate() {
  const [mrList, setMrList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFolders, setOpenFolders] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [openDeptGroups, setOpenDeptGroups] = useState({});
  const [selectedMR, setSelectedMR] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [websiteInfo, setWebsiteInfo] = useState(null);
  const [customLogo, setCustomLogo] = useState(null);
  const [showLogoUploader, setShowLogoUploader] = useState(false);
  const [exportingFolder, setExportingFolder] = useState(null);
  const [exportingDept, setExportingDept] = useState(null);
  const [exportingAll, setExportingAll] = useState(false);
  const [viewMode, setViewMode] = useState('date'); // 'date' | 'department'
  const [deptSubView, setDeptSubView] = useState({});

  useEffect(() => { fetchAll(); fetchWebsiteInfo(); }, []);

  // Auto-open current shift folder on load
  useEffect(() => {
    const now = new Date();
    const { key } = getShift(now.toISOString());
    setOpenFolders({ [key]: true });
  }, [mrList]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/MRHeader`);
      if (!res.ok) { setMrList([]); return; }
      const headers = await res.json();
      const arr = Array.isArray(headers) ? headers : [headers];
      const withDetails = await Promise.all(
        arr.map(async (h) => {
          try {
            const dr = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${h.id}`);
            const d = dr.ok ? await dr.json() : [];
            return { ...h, mrDetails: Array.isArray(d) ? d : [] };
          } catch { return { ...h, mrDetails: [] }; }
        })
      );
      setMrList(withDetails);
    } catch (e) { console.error(e); setMrList([]); }
    finally { setLoading(false); }
  };

  const fetchWebsiteInfo = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Website_Details`);
      if (!res.ok) return;
      const data = await res.json();
      const active = data.find(w => w.isActive) || data[0];
      if (active) setWebsiteInfo(active);
    } catch { }
  };

  const fetchUserSignature = async (userId) => {
    if (!userId) return null;
    try {
      const r = await fetch(`${API_BASE_URL}api/Users/${userId}/digitalsign`);
      if (!r.ok) return null;
      const blob = await r.blob();
      return await new Promise(resolve => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch { return null; }
  };

  // ── Filtered list ───────────────────────────────────────────────────────────
  const filteredList = useMemo(() => {
    return mrList.filter(mr => {
      const s = searchTerm.toLowerCase();
      const okSearch = !searchTerm || [mr.mrNo, mr.controlNo, mr.requestedByName, mr.department, mr.departmentName, mr.arInvoiceNo]
        .some(v => v?.toLowerCase().includes(s));
      const okStatus = statusFilter === 'all' || String(mr.status) === statusFilter;
      const dk = (getMRTimestamp(mr) || '').split('T')[0];
      return okSearch && okStatus && (!dateFrom || dk >= dateFrom) && (!dateTo || dk <= dateTo);
    });
  }, [mrList, searchTerm, statusFilter, dateFrom, dateTo]);

  // ── Group by SHIFT (12-hour) ────────────────────────────────────────────────
  const groupByShift = useMemo(() => {
    const groups = {};
    filteredList.forEach(mr => {
      const ts = getMRTimestamp(mr) || new Date().toISOString();
      const { key, label, shift, dateKey } = getShift(ts);
      if (!groups[key]) groups[key] = { key, label, shift, dateKey, mrs: [] };
      groups[key].mrs.push(mr);
    });
    // Sort descending: newest shift first
    return Object.values(groups).sort((a, b) => {
      if (b.dateKey !== a.dateKey) return b.dateKey.localeCompare(a.dateKey);
      // PM > AM on same date (PM is later in the day)
      return (b.shift === 'PM' ? 1 : 0) - (a.shift === 'PM' ? 1 : 0);
    });
  }, [filteredList]);

  // ── Group by DEPARTMENT ─────────────────────────────────────────────────────
  const groupByDept = useMemo(() => {
    const groups = {};
    filteredList.forEach(mr => {
      const dept = mr.department || mr.departmentName || 'Unknown';
      if (!groups[dept]) groups[dept] = [];
      groups[dept].push(mr);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredList]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const toggleFolder = key => setOpenFolders(p => ({ ...p, [key]: !p[key] }));
  const toggleDeptGroup = dept => setOpenDeptGroups(p => ({ ...p, [dept]: !p[dept] }));
  const toggleRow = id => setExpandedRows(p => ({ ...p, [id]: !p[id] }));

  const openDetailModal = async (mr) => {
    const reqSig = await fetchUserSignature(mr.requestedBy);
    const appSig = await fetchUserSignature(mr.approvedBy);
    const relSig = await fetchUserSignature(mr.releasedBy);
    setSelectedMR({ ...mr, requestedBySignature: reqSig, approvedBySignature: appSig, releasedBySignature: relSig });
    setShowModal(true);
  };

  const formatDate = d => { if (!d) return 'N/A'; return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); };
  const formatDateLong = d => { if (!d) return 'N/A'; return new Date(d).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }); };
  const formatTime = d => { if (!d) return ''; return new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }); };

  const nowKey = getShift(new Date().toISOString()).key;
  const isCurrentShift = key => key === nowKey;

  const getLogoSrc = () => customLogo || megalogo;

  const getStatusBadge = status => {
    const s = STATUS_MAP[String(status)] || STATUS_MAP['0'];
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${s.bg} ${s.text} ${s.border}`}>
        <s.Icon className="w-3 h-3" />{s.label}
      </span>
    );
  };

  const handleLogoUpload = e => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => { setCustomLogo(reader.result); setShowLogoUploader(false); };
    reader.readAsDataURL(file);
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // PDF HELPERS
  // ══════════════════════════════════════════════════════════════════════════════
  const addPDFHeader = (doc, y) => {
    const logoSrc = getLogoSrc();
    if (logoSrc) { try { doc.addImage(logoSrc, 'PNG', 12, y, 25, 12); } catch { } }
    doc.setFontSize(6); doc.setFont(undefined, 'normal'); doc.setTextColor(100, 100, 100);
    if (websiteInfo?.websiteSlogan) { doc.setFont(undefined, 'italic'); doc.text(websiteInfo.websiteSlogan, 40, y + 3); doc.setFont(undefined, 'normal'); }
    if (websiteInfo?.websiteName) doc.text(websiteInfo.websiteName, 40, y + 6);
    if (websiteInfo?.websiteContact) doc.text(websiteInfo.websiteContact, 40, y + 9);
    doc.setTextColor(0, 0, 0);
    doc.rect(170, y, 30, 12);
    doc.setFontSize(6); doc.setFont(undefined, 'normal');
    doc.text('Document Code:', 172, y + 5);
    doc.setFontSize(5.5); doc.text('SPR-F-04-SP-REQ.F-001', 172, y + 9);
    return y;
  };

  const addMRBlock = (doc, mr, y, giNumber = null) => {
    if (y > 210) { doc.addPage(); y = 12; }
    doc.setLineWidth(0.3); doc.setDrawColor(0, 0, 0);
    doc.rect(10, y, 35, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Date:', 12, y + 3);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(formatDate(getMRTimestamp(mr)), 12, y + 6);
    doc.rect(45, y, 70, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Name:', 47, y + 3);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(mr.requestedByName || 'N/A', 47, y + 6);
    doc.rect(115, y, 85, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Good Issuance No.:', 117, y + 3);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(giNumber || mr.arInvoiceNo || '—', 117, y + 6);
    y += 8;
    doc.rect(10, y, 105, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Department:', 12, y + 3);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(mr.department || mr.departmentName || 'N/A', 12, y + 6);
    doc.rect(115, y, 85, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Control No.:', 117, y + 3);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(mr.controlNo || 'N/A', 117, y + 6);
    y += 8;
    const deptVal = (mr.department || mr.departmentName || '').toLowerCase().trim();
    const isDept = d => deptVal === d.toLowerCase().trim() || deptVal.includes(d.toLowerCase().trim());
    doc.rect(10, y, 190, 6); doc.setFontSize(6); let xPos = 12;
    DEPT1.forEach(d => { doc.text(`${isDept(d) ? '[X]' : '[ ]'} ${d}`, xPos, y + 4); xPos += 27; });
    y += 6; doc.rect(10, y, 190, 6); xPos = 12;
    DEPT2.forEach(d => { doc.text(`${isDept(d) ? '[X]' : '[ ]'} ${d}`, xPos, y + 4); xPos += 27; });
    y += 6;
    if (mr.remarks) {
      doc.setFillColor(254, 243, 199); doc.rect(10, y, 190, 7, 'F'); doc.rect(10, y, 190, 7);
      doc.setFontSize(6); doc.setFont(undefined, 'bold'); doc.text('REMARKS:', 12, y + 2.5);
      doc.setFont(undefined, 'normal'); doc.setFontSize(5.5);
      const lines = doc.splitTextToSize(mr.remarks, 175);
      doc.text(lines, 12, y + 5.5); y += 7 + Math.max(0, (lines.length - 1) * 3.5);
    }
    y += 1;
    const tableData = (mr.mrDetails || []).map(item => [
      item.itemCode, item.requestedQty?.toString() || '0', item.uom,
      item.itemDescription?.length > 45 ? item.itemDescription.substring(0, 45) + '...' : item.itemDescription,
      item.purpose || '—'
    ]);
    if (tableData.length === 0) tableData.push(['—', '—', '—', 'No items', '—']);
    autoTable(doc, {
      startY: y,
      head: [['Item Code', 'Qty.', 'UOM', 'Item Description', 'Purpose']],
      body: tableData, theme: 'grid',
      headStyles: { fillColor: [240, 240, 240], textColor: 0, fontSize: 7, fontStyle: 'bold', halign: 'left', lineWidth: 0.3, lineColor: 0, cellPadding: 2 },
      bodyStyles: { fontSize: 6.5, lineWidth: 0.3, lineColor: 0, cellPadding: 2 },
      columnStyles: { 0: { cellWidth: 25 }, 1: { cellWidth: 12, halign: 'center' }, 2: { cellWidth: 12, halign: 'center' }, 3: { cellWidth: 90 }, 4: { cellWidth: 51 } },
      margin: { left: 10, right: 10 }
    });
    y = doc.lastAutoTable.finalY + 4;
    if (y > 250) { doc.addPage(); y = 12; }
    doc.rect(10, y, 95, 25); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Requested By:', 12, y + 4);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(mr.requestedByName || 'N/A', 12, y + 18);
    doc.line(12, y + 20, 100, y + 20); doc.setFontSize(5.5); doc.setTextColor(100, 100, 100);
    doc.text("(Requestor's Signature & Date)", 12, y + 23); doc.setTextColor(0, 0, 0);
    doc.rect(105, y, 95, 25); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Approved By:', 107, y + 4);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    if (mr.approvedByName) { doc.text(mr.approvedByName, 107, y + 16); if (mr.approvedDate) doc.text(formatDate(mr.approvedDate), 107, y + 19); }
    doc.line(107, y + 20, 195, y + 20); doc.setFontSize(5.5); doc.setTextColor(100, 100, 100);
    doc.text("(Supervisor/Immediate Supervisor) Signature & Date", 107, y + 23); doc.setTextColor(0, 0, 0);
    const relY = y + 25;
    doc.rect(10, relY, 190, 25); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Released By:', 12, relY + 4);
    doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
    if (mr.releasedByName) { doc.text(mr.releasedByName, 12, relY + 16); if (mr.releasedDate) doc.text(formatDate(mr.releasedDate), 12, relY + 19); }
    doc.line(12, relY + 20, 195, relY + 20); doc.setFontSize(5.5); doc.setTextColor(100, 100, 100);
    doc.text("(Spare Parts Personnel's) Signature & Date", 12, relY + 23); doc.setTextColor(0, 0, 0);
    return relY + 30;
  };

  // ── Export: Shift folder PDF ────────────────────────────────────────────────
  const exportFolderPDF = async (shiftKey, shiftLabel, dateKey, mrs) => {
    setExportingFolder(shiftKey);
    const doc = new jsPDF();
    doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.5);
    const logoSrc = getLogoSrc();

    const deptGroups = {};
    mrs.forEach(mr => {
      const dept = mr.department || mr.departmentName || 'Unknown';
      if (!deptGroups[dept]) deptGroups[dept] = [];
      deptGroups[dept].push(mr);
    });
    const deptEntries = Object.entries(deptGroups);

    const drawForm = (dept, deptMrs, isFirstPage) => {
      if (!isFirstPage) doc.addPage();
      const sorted = [...deptMrs].sort((a, b) => new Date(b.mrDate || 0) - new Date(a.mrDate || 0));
      const baseMR = sorted[0];
      const allItems = deptMrs.flatMap(mr => (mr.mrDetails || []).map(item => ({ ...item })));
      let y = 10;

      if (logoSrc) { try { doc.addImage(logoSrc, 'PNG', 12, y, 25, 12); } catch { } }
      doc.setFontSize(6); doc.setFont(undefined, 'normal'); doc.setTextColor(100, 100, 100);
      if (websiteInfo?.websiteSlogan) { doc.setFont(undefined, 'italic'); doc.text(websiteInfo.websiteSlogan, 40, y + 3); doc.setFont(undefined, 'normal'); }
      if (websiteInfo?.websiteName) doc.text(websiteInfo.websiteName, 40, y + 6);
      if (websiteInfo?.websiteContact) doc.text(websiteInfo.websiteContact, 40, y + 9);
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11); doc.setFont(undefined, 'bold');
      doc.text('Spare Parts Item Requests', 105, y + 6, { align: 'center' });
      doc.setFontSize(7); doc.setFont(undefined, 'normal');
      doc.text(`${shiftLabel} | ${formatDate(dateKey)}`, 105, y + 11, { align: 'center' });
      doc.rect(170, y, 30, 12);
      doc.setFontSize(6); doc.text('Document Code:', 172, y + 5);
      doc.setFontSize(5.5); doc.text('SPR-F-04-SP-REQ.F-001', 172, y + 9);
      y += 15; doc.setLineWidth(0.3);

      doc.rect(10, y, 35, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Date:', 12, y + 3);
      doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(formatDate(baseMR.mrDate), 12, y + 6);
      doc.rect(45, y, 70, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Name:', 47, y + 3);
      doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(baseMR.requestedByName || 'N/A', 47, y + 6);
      doc.rect(115, y, 85, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Good Issuance No.:', 117, y + 3);
      doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(getShiftGINumber(shiftKey), 117, y + 6);
      y += 8;
      doc.rect(10, y, 105, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Department:', 12, y + 3);
      doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(dept, 12, y + 6);
      doc.rect(115, y, 85, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Control No.:', 117, y + 3);
      doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(baseMR.controlNo || baseMR.mrNo || '—', 117, y + 6);
      y += 8;

      const deptVal = dept.toLowerCase().trim();
      const isDept = d => deptVal === d.toLowerCase().trim() || deptVal.includes(d.toLowerCase().trim());
      doc.rect(10, y, 190, 6); doc.setFontSize(6); let xPos = 12;
      DEPT1.forEach(d => { doc.text(`${isDept(d) ? '[X]' : '[ ]'} ${d}`, xPos, y + 4); xPos += 27; });
      y += 6; doc.rect(10, y, 190, 6); xPos = 12;
      DEPT2.forEach(d => { doc.text(`${isDept(d) ? '[X]' : '[ ]'} ${d}`, xPos, y + 4); xPos += 27; });
      y += 7;

      const tableData = allItems.map(item => [
        item.itemCode, item.requestedQty?.toString() || '0', item.uom,
        item.itemDescription?.length > 45 ? item.itemDescription.substring(0, 45) + '...' : item.itemDescription,
        item.purpose || '—'
      ]);
      if (tableData.length === 0) tableData.push(['—', '—', '—', 'No items', '—']);
      autoTable(doc, {
        startY: y,
        head: [['Item Code', 'Qty.', 'UOM', 'Item Description', 'Purpose']],
        body: tableData, theme: 'grid',
        headStyles: { fillColor: [240, 240, 240], textColor: 0, fontSize: 7, fontStyle: 'bold', lineWidth: 0.3, lineColor: 0, cellPadding: 2 },
        bodyStyles: { fontSize: 6.5, lineWidth: 0.3, lineColor: 0, cellPadding: 2 },
        columnStyles: { 0: { cellWidth: 25, fontStyle: 'bold' }, 1: { cellWidth: 12, halign: 'center' }, 2: { cellWidth: 12, halign: 'center' }, 3: { cellWidth: 90 }, 4: { cellWidth: 51 } },
        margin: { left: 10, right: 10 }
      });
      const finalY = doc.lastAutoTable.finalY + 4;
      const sigY = finalY > 250 ? (doc.addPage(), 12) : finalY;

      doc.rect(10, sigY, 95, 25); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Requested By:', 12, sigY + 4);
      doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(baseMR.requestedByName || 'N/A', 12, sigY + 18);
      doc.line(12, sigY + 20, 100, sigY + 20); doc.setFontSize(5.5); doc.setTextColor(100, 100, 100);
      doc.text("(Requestor's Signature & Date)", 12, sigY + 23); doc.setTextColor(0, 0, 0);
      doc.rect(105, sigY, 95, 25); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Approved By:', 107, sigY + 4);
      doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
      if (baseMR.approvedByName) { doc.text(baseMR.approvedByName, 107, sigY + 16); if (baseMR.approvedDate) doc.text(formatDate(baseMR.approvedDate), 107, sigY + 19); }
      doc.line(107, sigY + 20, 195, sigY + 20); doc.setFontSize(5.5); doc.setTextColor(100, 100, 100);
      doc.text("(Supervisor/Immediate Supervisor) Signature & Date", 107, sigY + 23); doc.setTextColor(0, 0, 0);
      const relY = sigY + 25;
      doc.rect(10, relY, 190, 25); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Released By:', 12, relY + 4);
      doc.setFont(undefined, 'normal'); doc.setFontSize(6.5);
      if (baseMR.releasedByName) { doc.text(baseMR.releasedByName, 12, relY + 16); if (baseMR.releasedDate) doc.text(formatDate(baseMR.releasedDate), 12, relY + 19); }
      doc.line(12, relY + 20, 195, relY + 20); doc.setFontSize(5.5); doc.setTextColor(100, 100, 100);
      doc.text("(Spare Parts Personnel's) Signature & Date", 12, relY + 23); doc.setTextColor(0, 0, 0);
      doc.setFontSize(6); doc.setTextColor(128, 128, 128);
      doc.text(`${dept} | ${shiftLabel} | ${formatDate(dateKey)} | ${allItems.length} items | Generated: ${new Date().toLocaleString()}`, 105, 287, { align: 'center' });
      doc.setTextColor(0, 0, 0);
    };

    deptEntries.forEach(([dept, deptMrs], idx) => drawForm(dept, deptMrs, idx === 0));
    doc.save(`MR_${shiftKey}.pdf`);
    const user = JSON.parse(localStorage.getItem("user"));
    await fetch(`${API_BASE_URL}api/Logs`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "EXPORT", module: "Material Request", description: `Shift folder exported: ${shiftLabel} ${dateKey}`, userId: user?.userId, username: user?.username, fullName: user?.fullName || user?.username })
    }).catch(() => { });
    setExportingFolder(null);
  };

  // ── Export: Per Dept PDF (unchanged) ───────────────────────────────────────
  const exportDeptPDF = async (dept, mrs) => {
    setExportingDept(dept);
    const doc = new jsPDF();
    doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.5);
    const sorted = [...mrs].sort((a, b) => new Date(b.mrDate || 0) - new Date(a.mrDate || 0));
    const baseMR = sorted[0];
    const allItems = mrs.flatMap(mr => (mr.mrDetails || []).map(item => ({ ...item })));
    const logoSrc = getLogoSrc();
    let y = 10;
    if (logoSrc) { try { doc.addImage(logoSrc, 'PNG', 12, y, 25, 12); } catch { } }
    doc.setFontSize(6); doc.setTextColor(100, 100, 100);
    if (websiteInfo?.websiteSlogan) { doc.setFont(undefined, 'italic'); doc.text(websiteInfo.websiteSlogan, 40, y + 3); doc.setFont(undefined, 'normal'); }
    if (websiteInfo?.websiteName) doc.text(websiteInfo.websiteName, 40, y + 6);
    if (websiteInfo?.websiteContact) doc.text(websiteInfo.websiteContact, 40, y + 9);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(13); doc.setFont(undefined, 'bold'); doc.text('Spare Parts Item Requests', 105, y + 8, { align: 'center' });
    doc.rect(170, y, 30, 12); doc.setFontSize(6); doc.setFont(undefined, 'normal'); doc.text('Document Code:', 172, y + 5); doc.setFontSize(5.5); doc.text('SPR-F-04-SP-REQ.F-001', 172, y + 9);
    y += 15; doc.setLineWidth(0.3);
    doc.rect(10, y, 35, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Date:', 12, y + 3); doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(formatDate(baseMR.mrDate), 12, y + 6);
    doc.rect(45, y, 70, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Name:', 47, y + 3); doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(baseMR.requestedByName || 'N/A', 47, y + 6);
    doc.rect(115, y, 85, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Good Issuance No.:', 117, y + 3); doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(baseMR.arInvoiceNo || '—', 117, y + 6);
    y += 8;
    doc.rect(10, y, 105, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Department:', 12, y + 3); doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(dept, 12, y + 6);
    doc.rect(115, y, 85, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Control No.:', 117, y + 3); doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(baseMR.controlNo || baseMR.mrNo || '—', 117, y + 6);
    y += 8;
    const deptVal = dept.toLowerCase().trim();
    const isDept = d => deptVal === d.toLowerCase().trim() || deptVal.includes(d.toLowerCase().trim());
    doc.rect(10, y, 190, 6); doc.setFontSize(6); let xPos = 12;
    DEPT1.forEach(d => { doc.text(`${isDept(d) ? '[X]' : '[ ]'} ${d}`, xPos, y + 4); xPos += 27; });
    y += 6; doc.rect(10, y, 190, 6); xPos = 12;
    DEPT2.forEach(d => { doc.text(`${isDept(d) ? '[X]' : '[ ]'} ${d}`, xPos, y + 4); xPos += 27; });
    y += 7;
    const tableData = allItems.map(item => [item.itemCode, item.requestedQty?.toString() || '0', item.uom, item.itemDescription?.length > 45 ? item.itemDescription.substring(0, 45) + '...' : item.itemDescription, item.purpose || '—']);
    if (tableData.length === 0) tableData.push(['—', '—', '—', 'No items', '—']);
    autoTable(doc, { startY: y, head: [['Item Code', 'Qty.', 'UOM', 'Item Description', 'Purpose']], body: tableData, theme: 'grid', headStyles: { fillColor: [240, 240, 240], textColor: 0, fontSize: 7, fontStyle: 'bold', lineWidth: 0.3, lineColor: 0, cellPadding: 2 }, bodyStyles: { fontSize: 6.5, lineWidth: 0.3, lineColor: 0, cellPadding: 2 }, columnStyles: { 0: { cellWidth: 25, fontStyle: 'bold' }, 1: { cellWidth: 12, halign: 'center' }, 2: { cellWidth: 12, halign: 'center' }, 3: { cellWidth: 90 }, 4: { cellWidth: 51 } }, margin: { left: 10, right: 10 } });
    const finalY = doc.lastAutoTable.finalY + 4;
    const sigY = finalY > 250 ? (doc.addPage(), 12) : finalY;
    doc.rect(10, sigY, 95, 25); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Requested By:', 12, sigY + 4); doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(baseMR.requestedByName || 'N/A', 12, sigY + 18); doc.line(12, sigY + 20, 100, sigY + 20); doc.setFontSize(5.5); doc.setTextColor(100, 100, 100); doc.text("(Requestor's Signature & Date)", 12, sigY + 23); doc.setTextColor(0, 0, 0);
    doc.rect(105, sigY, 95, 25); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Approved By:', 107, sigY + 4); doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); if (baseMR.approvedByName) { doc.text(baseMR.approvedByName, 107, sigY + 16); } doc.line(107, sigY + 20, 195, sigY + 20); doc.setFontSize(5.5); doc.setTextColor(100, 100, 100); doc.text("(Supervisor/Immediate Supervisor) Signature & Date", 107, sigY + 23); doc.setTextColor(0, 0, 0);
    const relY = sigY + 25;
    doc.rect(10, relY, 190, 25); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Released By:', 12, relY + 4); doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); if (baseMR.releasedByName) { doc.text(baseMR.releasedByName, 12, relY + 16); } doc.line(12, relY + 20, 195, relY + 20); doc.setFontSize(5.5); doc.setTextColor(100, 100, 100); doc.text("(Spare Parts Personnel's) Signature & Date", 12, relY + 23); doc.setTextColor(0, 0, 0);
    doc.setFontSize(6); doc.setTextColor(128, 128, 128); doc.text(`Generated on ${new Date().toLocaleString()} | Dept: ${dept} | ${allItems.length} items`, 105, 287, { align: 'center' });
    doc.save(`MR_Dept_${dept.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    const user = JSON.parse(localStorage.getItem("user"));
    await fetch(`${API_BASE_URL}api/Logs`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "EXPORT", module: "Material Request", description: `Dept MR exported: ${dept}`, userId: user?.userId, username: user?.username, fullName: user?.fullName || user?.username }) }).catch(() => { });
    setExportingDept(null);
  };

  // ── Export: All depts PDF ───────────────────────────────────────────────────
  const exportAllDeptsPDF = async () => {
    setExportingAll(true);
    const doc = new jsPDF();
    doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.5);
    const logoSrc = getLogoSrc();
    let y = 10;
    if (logoSrc) { try { doc.addImage(logoSrc, 'PNG', 12, y, 25, 12); } catch { } }
    doc.setFontSize(13); doc.setFont(undefined, 'bold'); doc.text('Spare Parts Item Requests — All Departments', 105, y + 5, { align: 'center' });
    doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.text(`Generated: ${new Date().toLocaleString()}`, 105, y + 11, { align: 'center' });
    y += 18;
    autoTable(doc, { startY: y, head: [['Department', 'Requests', 'Total Items']], body: groupByDept.map(([dept, mrs]) => [dept, mrs.length, mrs.reduce((a, m) => a + (m.mrDetails?.length || 0), 0)]), theme: 'grid', headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 8, fontStyle: 'bold', cellPadding: 3 }, bodyStyles: { fontSize: 8, cellPadding: 3 }, columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' } }, margin: { left: 10, right: 10 } });
    for (let di = 0; di < groupByDept.length; di++) {
      const [dept, mrs] = groupByDept[di];
      doc.addPage();
      const sorted = [...mrs].sort((a, b) => new Date(b.mrDate || 0) - new Date(a.mrDate || 0));
      const baseMR = sorted[0];
      const allItems = mrs.flatMap(mr => (mr.mrDetails || []).map(item => ({ ...item })));
      y = 10;
      if (logoSrc) { try { doc.addImage(logoSrc, 'PNG', 12, y, 25, 12); } catch { } }
      doc.setFontSize(13); doc.setFont(undefined, 'bold'); doc.text('Spare Parts Item Requests', 105, y + 8, { align: 'center' });
      doc.rect(170, y, 30, 12); doc.setFontSize(6); doc.setFont(undefined, 'normal'); doc.text('Document Code:', 172, y + 5); doc.setFontSize(5.5); doc.text('SPR-F-04-SP-REQ.F-001', 172, y + 9);
      y += 15; doc.setLineWidth(0.3);
      doc.rect(10, y, 35, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Date:', 12, y + 3); doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(formatDate(baseMR.mrDate), 12, y + 6);
      doc.rect(45, y, 70, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Name:', 47, y + 3); doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(baseMR.requestedByName || 'N/A', 47, y + 6);
      doc.rect(115, y, 85, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Good Issuance No.:', 117, y + 3); doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(baseMR.arInvoiceNo || '—', 117, y + 6);
      y += 8;
      doc.rect(10, y, 105, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Department:', 12, y + 3); doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(dept, 12, y + 6);
      doc.rect(115, y, 85, 8); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Control No.:', 117, y + 3); doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(baseMR.controlNo || baseMR.mrNo || '—', 117, y + 6);
      y += 8;
      const deptVal = dept.toLowerCase().trim();
      const isDept = d => deptVal === d.toLowerCase().trim() || deptVal.includes(d.toLowerCase().trim());
      doc.rect(10, y, 190, 6); doc.setFontSize(6); let xPos = 12;
      DEPT1.forEach(d => { doc.text(`${isDept(d) ? '[X]' : '[ ]'} ${d}`, xPos, y + 4); xPos += 27; });
      y += 6; doc.rect(10, y, 190, 6); xPos = 12;
      DEPT2.forEach(d => { doc.text(`${isDept(d) ? '[X]' : '[ ]'} ${d}`, xPos, y + 4); xPos += 27; });
      y += 7;
      const tableData = allItems.map(item => [item.itemCode, item.requestedQty?.toString() || '0', item.uom, item.itemDescription?.length > 45 ? item.itemDescription.substring(0, 45) + '...' : item.itemDescription, item.purpose || '—']);
      if (tableData.length === 0) tableData.push(['—', '—', '—', 'No items', '—']);
      autoTable(doc, { startY: y, head: [['Item Code', 'Qty.', 'UOM', 'Item Description', 'Purpose']], body: tableData, theme: 'grid', headStyles: { fillColor: [240, 240, 240], textColor: 0, fontSize: 7, fontStyle: 'bold', lineWidth: 0.3, lineColor: 0, cellPadding: 2 }, bodyStyles: { fontSize: 6.5, lineWidth: 0.3, lineColor: 0, cellPadding: 2 }, columnStyles: { 0: { cellWidth: 25, fontStyle: 'bold' }, 1: { cellWidth: 12, halign: 'center' }, 2: { cellWidth: 12, halign: 'center' }, 3: { cellWidth: 90 }, 4: { cellWidth: 51 } }, margin: { left: 10, right: 10 } });
      const finalY = doc.lastAutoTable.finalY + 4;
      const sigY = finalY > 250 ? (doc.addPage(), 12) : finalY;
      doc.rect(10, sigY, 95, 25); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Requested By:', 12, sigY + 4); doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); doc.text(baseMR.requestedByName || 'N/A', 12, sigY + 18); doc.line(12, sigY + 20, 100, sigY + 20); doc.setFontSize(5.5); doc.setTextColor(100, 100, 100); doc.text("(Requestor's Signature & Date)", 12, sigY + 23); doc.setTextColor(0, 0, 0);
      doc.rect(105, sigY, 95, 25); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Approved By:', 107, sigY + 4); doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); if (baseMR.approvedByName) doc.text(baseMR.approvedByName, 107, sigY + 16); doc.line(107, sigY + 20, 195, sigY + 20); doc.setFontSize(5.5); doc.setTextColor(100, 100, 100); doc.text("(Supervisor/Immediate Supervisor) Signature & Date", 107, sigY + 23); doc.setTextColor(0, 0, 0);
      const relY = sigY + 25;
      doc.rect(10, relY, 190, 25); doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.text('Released By:', 12, relY + 4); doc.setFont(undefined, 'normal'); doc.setFontSize(6.5); if (baseMR.releasedByName) doc.text(baseMR.releasedByName, 12, relY + 16); doc.line(12, relY + 20, 195, relY + 20); doc.setFontSize(5.5); doc.setTextColor(100, 100, 100); doc.text("(Spare Parts Personnel's) Signature & Date", 12, relY + 23); doc.setTextColor(0, 0, 0);
      doc.setFontSize(6); doc.setTextColor(128, 128, 128); doc.text(`Dept: ${dept} | ${allItems.length} items`, 105, 287, { align: 'center' }); doc.setTextColor(0, 0, 0);
    }
    doc.save(`MR_AllDepartments_${new Date().toISOString().split('T')[0]}.pdf`);
    const user = JSON.parse(localStorage.getItem("user"));
    await fetch(`${API_BASE_URL}api/Logs`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "EXPORT", module: "Material Request", description: `All Departments MR exported`, userId: user?.userId, username: user?.username, fullName: user?.fullName || user?.username }) }).catch(() => { });
    setExportingAll(false);
  };

  const exportSinglePDF = (mr) => {
    const doc = new jsPDF();
    doc.setDrawColor(0, 0, 0); doc.setLineWidth(0.5);
    let y = 10;
    addPDFHeader(doc, y);
    doc.setFontSize(13); doc.setFont(undefined, 'bold'); doc.text('Spare Parts Item Requests', 105, y + 8, { align: 'center' });
    y += 15; doc.setLineWidth(0.3);
    const mrShiftGI = getShiftGINumber(getShift(getMRTimestamp(mr)).key);
    y = addMRBlock(doc, mr, y, mrShiftGI);
    doc.setFontSize(6); doc.setTextColor(128, 128, 128); doc.text(`Generated on ${new Date().toLocaleString()}`, 105, 287, { align: 'center' });
    doc.save(`SPR_${mr.controlNo || mr.mrNo}_${new Date().toISOString().split('T')[0]}.pdf`);
    const user = JSON.parse(localStorage.getItem("user"));
    fetch(`${API_BASE_URL}api/Logs`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "EXPORT", module: "Material Request", description: `MR ${mr.mrNo} exported`, userId: user?.userId, username: user?.username, fullName: user?.fullName || user?.username }) }).catch(() => { });
  };

  // ── GI Number Generator (per 12-hour shift) ────────────────────────────────
  const getShiftGINumber = (shiftKey) => {
    const STORAGE_KEY = 'shift_gi_map';
    const COUNT_KEY = 'shift_gi_count';
    try {
      const map = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      if (map[shiftKey]) return map[shiftKey];
      const count = parseInt(localStorage.getItem(COUNT_KEY) || '0') + 1;
      const gi = `GI${String(count).padStart(6, '0')}`;
      map[shiftKey] = gi;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
      localStorage.setItem(COUNT_KEY, String(count));
      return gi;
    } catch {
      return `GI${shiftKey.replace(/[^0-9]/g, '').slice(0, 6).padStart(6, '0')}`;
    }
  };

  const hasFilters = searchTerm || statusFilter !== 'all' || dateFrom || dateTo;
  const logoSrc = getLogoSrc();
  // ── Reusable MR Table ───────────────────────────────────────────────────────
  const MRTable = ({ mrs }) => (
    <div className="bg-white border-t border-slate-100">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gradient-to-r from-blue-600 to-blue-700">
              <th className="px-5 py-3.5 w-12"></th>
              <th className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wider">Time</th>
              <th className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wider">Name</th>
              <th className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wider">Good Issuance No.</th>
              <th className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wider">Department</th>
              <th className="px-5 py-3.5 text-left text-xs font-bold text-white uppercase tracking-wider">Control No.</th>
              <th className="px-5 py-3.5 text-center text-xs font-bold text-white uppercase tracking-wider">Items</th>
              <th className="px-5 py-3.5 text-center text-xs font-bold text-white uppercase tracking-wider">Status</th>
              <th className="px-5 py-3.5 text-center text-xs font-bold text-white uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {mrs.map(mr => {
              const rowOpen = !!expandedRows[mr.id];
              return (
                <React.Fragment key={mr.id}>
                  <tr className={`transition-colors ${rowOpen ? 'bg-slate-50' : 'hover:bg-gray-50'}`}>
                    <td className="px-4 py-4 text-center">
                      <button onClick={() => toggleRow(mr.id)}
                        className={`p-1.5 rounded-lg border transition-all ${rowOpen ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-400 hover:border-blue-400 hover:text-blue-600'}`}>
                        <ChevronRight className={`w-4 h-4 transition-transform ${rowOpen ? 'rotate-90' : ''}`} />
                      </button>
                    </td>
                    {/* Show TIME instead of full date since the date is in the folder header */}
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2 text-slate-700 text-sm">
                        <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <div>
                          <p className="font-semibold">{formatTime(getMRTimestamp(mr))}</p>
                          <p className="text-xs text-slate-400">{formatDate(getMRTimestamp(mr))}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="text-sm font-medium text-slate-800">{mr.requestedByName || '—'}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4"><span className="text-sm font-mono text-slate-600">{mr.arInvoiceNo || '—'}</span></td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2 text-sm text-slate-700">
                        <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />{mr.department || mr.departmentName || 'N/A'}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 border border-slate-200 px-2.5 py-1 rounded-lg text-xs font-medium">
                        <FileText className="w-3.5 h-3.5" />{mr.controlNo || mr.mrNo || '—'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-500 border border-slate-200 px-2.5 py-1 rounded-full text-xs font-medium">
                        <Package className="w-3 h-3" />{mr.mrDetails?.length || 0}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-center">{getStatusBadge(mr.status)}</td>
                    <td className="px-5 py-4 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <button onClick={() => openDetailModal(mr)} className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition-all shadow-sm hover:shadow">

                          <Eye className="w-3.5 h-3.5" /> View
                        </button>
                        <button onClick={() => exportSinglePDF(mr)} className="p-2 bg-white hover:bg-slate-50 text-slate-500 border border-slate-200 rounded-lg transition-all" title="Export PDF">
                          <Download className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {rowOpen && (
                    <tr>
                      <td colSpan={9} className="p-0 bg-slate-50 border-b border-slate-100">
                        <div className="mx-5 my-3 rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2.5 flex items-center gap-2">
                            <Package className="w-4 h-4 text-white" />
                            <span className="text-white text-sm font-semibold">Items — {mr.controlNo || mr.mrNo}</span>
                            <span className="ml-auto px-2.5 py-0.5 bg-white/20 text-white text-xs rounded-full font-bold">{mr.mrDetails?.length || 0} items</span>
                          </div>
                          {!mr.mrDetails || mr.mrDetails.length === 0 ? (
                            <div className="bg-white px-6 py-6 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
                              <AlertCircle className="w-4 h-4" /> No items found.
                            </div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-slate-50 border-b border-slate-100">
                                  <tr>{['Item Code', 'Description', 'Qty', 'UOM', 'Issued', 'Returned', 'Warehouse', 'Purpose'].map(h => (
                                    <th key={h} className="px-4 py-2.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">{h}</th>
                                  ))}</tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-slate-50">
                                  {mr.mrDetails.map((item, idx) => (
                                    <tr key={item.id || idx} className="hover:bg-slate-50/50 transition-colors">
                                      <td className="px-4 py-2.5 font-mono font-bold text-slate-700 text-sm">{item.itemCode}</td>
                                      <td className="px-4 py-2.5 text-sm text-slate-700">{item.itemDescription}</td>
                                      <td className="px-4 py-2.5 text-sm font-bold text-slate-800 text-center">{item.requestedQty}</td>
                                      <td className="px-4 py-2.5 text-sm text-slate-500 text-center">{item.uom}</td>
                                      <td className="px-4 py-2.5 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${item.issuedQty > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>{item.issuedQty ?? 0}</span></td>
                                      <td className="px-4 py-2.5 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${(item.returnedQty || 0) > 0 ? 'bg-orange-50 text-orange-700' : 'bg-slate-100 text-slate-400'}`}>{item.returnedQty ?? 0}</span></td>
                                      <td className="px-4 py-2.5 text-sm text-slate-500">{item.warehouseCode || '—'}</td>
                                      <td className="px-4 py-2.5 text-sm text-slate-500">{item.purpose || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-[1800px] mx-auto">

        {/* ── Header ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-5">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-4">
              <div className="bg-slate-700 p-3.5 rounded-xl shadow-sm">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Item Request Folders</h1>
                <p className="text-slate-500 text-sm mt-0.5">Grouped by 12-hour shift (AM 6:00–18:00 / PM 18:00–6:00) or by department</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={exportAllDeptsPDF} disabled={exportingAll || filteredList.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-700 hover:bg-slate-800 text-white rounded-xl font-medium text-sm transition-all shadow-sm disabled:opacity-60">
                {exportingAll ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {exportingAll ? 'Exporting...' : 'Export All Depts'}
              </button>
              <button onClick={fetchAll}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-medium text-sm transition-all border border-slate-200">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            {[
              { label: 'Draft', count: mrList.filter(m => m.status === 0).length, top: 'border-t-slate-400', num: 'text-slate-700' },
              { label: 'Pending', count: mrList.filter(m => m.status === 1).length, top: 'border-t-slate-400', num: 'text-slate-700' },
              { label: 'Approved', count: mrList.filter(m => m.status === 2).length, top: 'border-t-emerald-400', num: 'text-emerald-700' },
              { label: 'Issued', count: mrList.filter(m => m.status === 3).length, top: 'border-t-blue-500', num: 'text-slate-600' },
              { label: 'Rejected', count: mrList.filter(m => m.status === -1).length, top: 'border-t-red-400', num: 'text-red-700' },
            ].map(({ label, count, top, num }) => (
              <div key={label} className={`bg-white border border-slate-200 border-t-4 ${top} rounded-xl px-4 py-4 shadow-sm`}>
                <p className="text-slate-500 text-xs font-semibold uppercase tracking-wide mb-1">{label}</p>
                <p className={`text-3xl font-bold ${num}`}>{count}</p>
              </div>
            ))}
          </div>

          {/* View Toggle + Filters */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex items-center bg-slate-100 rounded-xl p-1 flex-shrink-0">
              <button onClick={() => setViewMode('date')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${viewMode === 'date' ? 'bg-white text-slate-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                <Clock className="w-4 h-4" /> By Shift
              </button>
              <button onClick={() => setViewMode('department')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${viewMode === 'department' ? 'bg-white text-slate-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                <Building2 className="w-4 h-4" /> By Department
              </button>
            </div>
            <div className="flex-1 relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input type="text" placeholder="Search by Control No, Good Issuance No, Name, Department..."
                value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:border-slate-300 focus:ring-2 focus:ring-slate-100 focus:outline-none text-sm bg-white text-slate-700 placeholder-slate-400 transition-all" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-2.5 border border-slate-200 rounded-xl focus:border-slate-300 focus:outline-none bg-white text-sm text-slate-700 min-w-[145px] transition-all">
              <option value="all">All Status</option>
              <option value="0">Draft</option>
              <option value="1">Pending</option>
              <option value="2">Approved</option>
              <option value="3">Issued</option>
              <option value="-1">Rejected</option>
            </select>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="px-3 py-2.5 border border-slate-200 rounded-xl focus:border-slate-300 focus:outline-none bg-white text-sm text-slate-700 transition-all" />
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="px-3 py-2.5 border border-slate-200 rounded-xl focus:border-slate-300 focus:outline-none bg-white text-sm text-slate-700 transition-all" />
            {hasFilters && (
              <button onClick={() => { setSearchTerm(''); setStatusFilter('all'); setDateFrom(''); setDateTo(''); }}
                className="px-4 py-2.5 text-sm font-medium text-red-500 border border-red-200 bg-red-50 hover:bg-red-100 rounded-xl transition-all">
                ✕ Clear
              </button>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="w-12 h-12 border-4 border-slate-400 border-t-transparent rounded-full animate-spin" />
            <p className="text-slate-500 text-sm font-medium">Loading Material Requests…</p>
          </div>
        )}

        {!loading && filteredList.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-slate-400">
            <AlertCircle className="w-14 h-14" />
            <p className="font-semibold text-lg text-slate-500">No material requests found</p>
          </div>
        )}

        {/* ════════════════════════════════════════════
            VIEW MODE: BY SHIFT (12-hour)
        ════════════════════════════════════════════ */}
        {!loading && viewMode === 'date' && (
          <div className="space-y-4">
            {groupByShift.map(({ key, label, shift, dateKey, mrs }) => {
              const open = !!openFolders[key];
              const isCurrent = isCurrentShift(key);
              const isAM = shift === 'AM';
              const totalItems = mrs.reduce((a, m) => a + (m.mrDetails?.length || 0), 0);
              const isExp = exportingFolder === key;
              const shiftGI = getShiftGINumber(key);

              // Clean neutral palette — dark header when open/active
              const headerBg = open || isCurrent ? 'bg-gradient-to-r from-blue-600 to-blue-700' : 'bg-white';
              const borderCol = isCurrent && !open ? 'border-blue-400' : 'border-gray-200';
              const textColor = open || isCurrent ? 'text-white' : 'text-gray-800';
              const subText = open || isCurrent ? 'text-blue-200' : 'text-gray-400';
              const iconColor = open || isCurrent ? 'text-blue-200' : 'text-blue-400';
              const badgeBg = open || isCurrent ? 'bg-white/20 border-white/20 text-white' : 'bg-blue-100 text-blue-700 border-blue-200';
              const accentL = isAM ? 'border-l-4 border-l-blue-400' : 'border-l-4 border-l-blue-600';

              return (
                <div key={key} className={`rounded-xl overflow-hidden border transition-all duration-200 ${borderCol} ${accentL} ${open || isCurrent ? 'shadow-sm shadow-slate-200' : 'shadow-sm hover:shadow-md hover:border-slate-300'}`}>
                  <div className={`flex items-stretch ${headerBg} transition-colors duration-200`}>
                    <button onClick={() => toggleFolder(key)} className="flex-1 flex items-center gap-4 px-6 py-4 text-left hover:opacity-90 transition-all">
                      <div className={`flex-shrink-0 ${iconColor}`}>
                        {open ? <FolderOpen className="w-6 h-6" /> : isAM ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-sm font-semibold ${textColor}`}>
                            {isAM ? 'AM Shift' : 'PM Shift'}
                          </span>
                          <span className={`text-xs ${subText}`}>
                            {isAM ? '6:00 AM – 6:00 PM' : '6:00 PM – 6:00 AM'}
                          </span>
                          {isCurrent && (
                            <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs font-semibold rounded-full">
                              Active
                            </span>
                          )}
                        </div>
                        <p className={`text-xs mt-0.5 ${subText}`}>{formatDateLong(dateKey)}</p>
                      </div>
                      {/* GI Number Display */}
                      <div className={`flex items-center gap-2 px-4 py-2 rounded-xl border font-mono font-bold text-sm flex-shrink-0 ${open || isCurrent ? 'bg-white/15 border-white/30 text-white' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
                        <Hash className="w-4 h-4" />
                        {shiftGI}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <p className={`text-xs mt-0.5 ${subText}`}>{formatDateLong(dateKey)}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border ${badgeBg}`}>
                          <FileText className="w-3.5 h-3.5" />{mrs.length} Request{mrs.length !== 1 ? 's' : ''}
                        </div>
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border ${badgeBg}`}>
                          <Package className="w-3.5 h-3.5" />{totalItems} Item{totalItems !== 1 ? 's' : ''}
                        </div>
                      </div>
                      <ChevronDown className={`w-5 h-5 transition-transform duration-200 flex-shrink-0 ${open ? 'rotate-180' : ''} ${subText}`} />
                    </button>
                    <button
                      onClick={() => exportFolderPDF(key, label, dateKey, mrs)}
                      disabled={isExp}
                      title={`Export all ${mrs.length} requests in this shift as PDF`}
                      className={`flex-shrink-0 flex items-center gap-2 px-5 border-l text-sm transition-all ${open || isCurrent ? 'border-white/10 text-slate-400 hover:bg-white/5 disabled:opacity-50' : 'border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50'}`}>
                      {isExp ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      <span className="hidden md:inline text-xs">{isExp ? 'Exporting...' : 'Export'}</span>
                    </button>
                  </div>

                  {/* Collapsed preview: show department summary */}
                  {!open && mrs.length > 0 && (
                    <div className="bg-slate-50 border-t border-slate-100 px-6 py-2.5 flex flex-wrap gap-1.5 items-center">
                      {(() => {
                        const depts = {};
                        mrs.forEach(mr => { const d = mr.department || mr.departmentName || 'Unknown'; depts[d] = (depts[d] || 0) + 1; });
                        return Object.entries(depts).map(([dept, count]) => (
                          <span key={dept} className="text-xs bg-white border border-slate-200 text-slate-600 px-2.5 py-1 rounded-full font-medium">
                            {dept} <span className="text-slate-400">({count})</span>
                          </span>
                        ));
                      })()}
                    </div>
                  )}

                  {open && <MRTable mrs={mrs} />}
                </div>
              );
            })}
          </div>
        )}

        {/* ════════════════════════════════════════════
            VIEW MODE: BY DEPARTMENT (unchanged)
        ════════════════════════════════════════════ */}
        {!loading && viewMode === 'department' && (
          <div className="space-y-4">
            {groupByDept.map(([dept, mrs]) => {
              const open = !!openDeptGroups[dept];
              const totalItems = mrs.reduce((a, m) => a + (m.mrDetails?.length || 0), 0);
              const isExp = exportingDept === dept;
              const requestors = [...new Set(mrs.map(m => m.requestedByName).filter(Boolean))];

              return (
                <div key={dept} className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200">
                  <div className={`flex items-stretch ${open ? 'bg-gradient-to-r from-blue-600 to-blue-700' : 'bg-white'}`}>

                    <button onClick={() => toggleDeptGroup(dept)} className="flex-1 flex items-center gap-4 px-6 py-4 text-left hover:opacity-90 transition-all">
                      <div className={`flex-shrink-0 ${open ? 'text-slate-300' : 'text-slate-400'}`}>
                        <Building2 className="w-8 h-8" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={`text-lg font-bold ${open ? 'text-white' : 'text-slate-700'}`}>{dept}</span>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <Users className={`w-3.5 h-3.5 flex-shrink-0 ${open ? 'text-slate-300' : 'text-slate-400'}`} />
                          {requestors.map(name => (
                            <span key={name} className={`text-xs px-2 py-0.5 rounded-full font-medium ${open ? 'bg-white/10 text-slate-300' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>{name}</span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold ${open ? 'bg-white/10 border-white/10 text-slate-300' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>
                          <FileText className="w-3.5 h-3.5" />{mrs.length} Request{mrs.length !== 1 ? 's' : ''}
                        </div>
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold ${open ? 'bg-white/10 border-white/10 text-slate-300' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                          <Package className="w-3.5 h-3.5" />{totalItems} Item{totalItems !== 1 ? 's' : ''}
                        </div>
                      </div>
                      <ChevronDown className={`w-5 h-5 transition-transform duration-200 flex-shrink-0 ${open ? 'rotate-180' : ''} ${open ? 'text-slate-300' : 'text-slate-400'}`} />
                    </button>
                    <button onClick={() => exportDeptPDF(dept, mrs)} disabled={isExp}
                      className={`flex-shrink-0 flex items-center gap-2 px-5 border-l font-semibold text-sm transition-all ${open ? 'border-white/10 text-slate-400 hover:bg-white/5 disabled:opacity-50' : 'border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50'}`}>
                      {isExp ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      <span className="hidden md:inline">{isExp ? 'Exporting...' : 'Export'}</span>
                    </button>
                  </div>

                  {!open && totalItems > 0 && (
                    <div className="bg-slate-50 border-t border-slate-100 px-6 py-2.5 flex flex-wrap gap-1.5">
                      {mrs.flatMap(mr => (mr.mrDetails || [])).slice(0, 10).map((item, i) => (
                        <span key={i} className="text-xs bg-white border border-slate-200 text-slate-600 px-2 py-0.5 rounded-full font-mono">{item.itemCode}</span>
                      ))}
                      {totalItems > 10 && <span className="text-xs text-slate-400 px-2 py-0.5">+{totalItems - 10} more items…</span>}
                    </div>
                  )}

                  {open && (() => {
                    const subView = deptSubView[dept] || 'table';
                    const setSubView = v => setDeptSubView(p => ({ ...p, [dept]: v }));
                    const itemGroups = {};
                    mrs.forEach(mr => {
                      (mr.mrDetails || []).forEach(item => {
                        const key = item.itemCode;
                        if (!itemGroups[key]) itemGroups[key] = { itemCode: item.itemCode, itemDescription: item.itemDescription, uom: item.uom, requestors: [] };
                        itemGroups[key].requestors.push({ name: mr.requestedByName, controlNo: mr.controlNo || mr.mrNo, qty: item.requestedQty, purpose: item.purpose, status: mr.status, date: mr.mrDate });
                      });
                    });
                    const itemList = Object.values(itemGroups).sort((a, b) => a.itemCode.localeCompare(b.itemCode));
                    const dupes = itemList.filter(g => g.requestors.length > 1);
                    const reqGroups = {};
                    mrs.forEach(mr => { const name = mr.requestedByName || 'Unknown'; if (!reqGroups[name]) reqGroups[name] = { name, mrs: [] }; reqGroups[name].mrs.push(mr); });
                    const reqList = Object.values(reqGroups).sort((a, b) => a.name.localeCompare(b.name));
                    return (
                      <div>
                        <div className="bg-slate-50 border-t border-slate-200 px-6 py-3 flex items-center gap-3 flex-wrap">
                          <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">View:</span>
                          <div className="flex items-center bg-white rounded-xl border border-slate-200 p-1 gap-0.5">
                            {[{ key: 'table', label: '📋 MR Table' }, { key: 'items', label: '📦 By Item' }, { key: 'requestors', label: '👤 By Requestor' }].map(tab => (
                              <button key={tab.key} onClick={() => setSubView(tab.key)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${subView === tab.key ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-blue-700 hover:bg-blue-50'}`}>
                                {tab.label}
                              </button>
                            ))}
                          </div>
                          {dupes.length > 0 && <span className="px-2.5 py-1 bg-orange-50 text-orange-700 border border-orange-200 text-xs font-bold rounded-full">⚠ {dupes.length} item{dupes.length > 1 ? 's' : ''} requested by multiple people</span>}
                          <span className="ml-auto text-xs text-slate-400">{itemList.length} unique items · {mrs.length} requests</span>
                        </div>
                        {subView === 'table' && <MRTable mrs={mrs} />}
                        {subView === 'items' && (
                          <div className="bg-white px-6 py-4 space-y-3">
                            {itemList.map(grp => (
                              <div key={grp.itemCode} className={`rounded-xl border overflow-hidden shadow-sm ${grp.requestors.length > 1 ? 'border-amber-200' : 'border-slate-200'}`}>
                                <div className={`flex items-center gap-3 px-4 py-3 ${grp.requestors.length > 1 ? 'bg-amber-50' : 'bg-slate-50'}`}>
                                  <span className={`font-mono font-bold text-sm ${grp.requestors.length > 1 ? 'text-amber-800' : 'text-slate-700'}`}>{grp.itemCode}</span>
                                  <span className="text-slate-600 text-sm flex-1">{grp.itemDescription}</span>
                                  <span className="text-xs text-slate-400 border border-slate-200 px-2 py-0.5 rounded bg-white">{grp.uom}</span>
                                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${grp.requestors.length > 1 ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-slate-100 text-slate-700 border-slate-300'}`}>{grp.requestors.length} requestor{grp.requestors.length > 1 ? 's' : ''}</span>
                                  <span className="px-2.5 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-bold border border-slate-200">Total qty: {grp.requestors.reduce((a, r) => a + Number(r.qty || 0), 0)}</span>
                                </div>
                                <div className="divide-y divide-slate-50">
                                  {grp.requestors.map((r, i) => {
                                    const sb = STATUS_MAP[String(r.status)] || STATUS_MAP['0'];
                                    return (
                                      <div key={i} className="flex items-center gap-3 px-4 py-2.5 text-xs bg-white hover:bg-slate-50 transition-colors">
                                        <div className="w-5 h-5 bg-slate-600 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">{i + 1}</div>
                                        <span className="font-semibold text-slate-700 w-44 truncate">{r.name}</span>
                                        <span className="font-mono text-slate-700 bg-slate-50 px-2 py-0.5 rounded border border-slate-200">{r.controlNo}</span>
                                        <span className="text-slate-500">Qty: <b className="text-slate-800">{r.qty}</b></span>
                                        <span className="text-slate-400">{formatDate(r.date)}</span>
                                        {r.purpose && <span className="text-slate-400 italic truncate flex-1 max-w-xs">"{r.purpose}"</span>}
                                        <span className={`px-2 py-0.5 rounded-full font-bold border text-xs ml-auto flex-shrink-0 ${sb.bg} ${sb.text} ${sb.border}`}>{sb.label}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {subView === 'requestors' && (
                          <div className="bg-white px-6 py-4 space-y-3">
                            {reqList.map(({ name, mrs: reqMrs }) => {
                              const reqItems = reqMrs.flatMap(mr => (mr.mrDetails || []).map(item => ({ ...item, controlNo: mr.controlNo || mr.mrNo, status: mr.status, mrDate: mr.mrDate })));
                              return (
                                <div key={name} className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                                  <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border-b border-slate-100">
                                    <div className="w-8 h-8 bg-slate-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">{name.charAt(0)}</div>
                                    <div className="flex-1"><p className="font-bold text-slate-800 text-sm">{name}</p><p className="text-xs text-slate-500">{reqMrs.length} request{reqMrs.length > 1 ? 's' : ''} · {reqItems.length} items</p></div>
                                    <div className="flex gap-1.5 flex-wrap justify-end">{reqMrs.map(mr => { const sb = STATUS_MAP[String(mr.status)] || STATUS_MAP['0']; return <span key={mr.id} className={`px-2 py-0.5 rounded-full text-xs font-bold border ${sb.bg} ${sb.text} ${sb.border}`}>{sb.label}</span>; })}</div>
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full">
                                      <thead className="bg-slate-50 border-b border-slate-100"><tr>{['Control No.', 'Item Code', 'Description', 'Qty', 'UOM', 'Purpose', 'Date'].map(h => <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-slate-400 uppercase tracking-wide">{h}</th>)}</tr></thead>
                                      <tbody className="divide-y divide-slate-50">{reqItems.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-slate-50/30 transition-colors">
                                          <td className="px-4 py-2 text-xs font-mono text-slate-600 font-bold">{item.controlNo}</td>
                                          <td className="px-4 py-2 text-xs font-mono font-bold text-slate-800">{item.itemCode}</td>
                                          <td className="px-4 py-2 text-xs text-slate-700">{item.itemDescription}</td>
                                          <td className="px-4 py-2 text-xs font-bold text-slate-800 text-center">{item.requestedQty}</td>
                                          <td className="px-4 py-2 text-xs text-slate-500 text-center">{item.uom}</td>
                                          <td className="px-4 py-2 text-xs text-slate-500 italic">{item.purpose || '—'}</td>
                                          <td className="px-4 py-2 text-xs text-slate-400">{formatDate(item.mrDate)}</td>
                                        </tr>
                                      ))}</tbody>
                                    </table>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════
          MODAL
      ════════════════════════════════════════════ */}
      {showModal && selectedMR && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-white border-b-2 border-slate-200 px-8 py-6 rounded-t-2xl flex-shrink-0">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative inline-block group">
                    {logoSrc ? (
                      <div className="flex flex-col items-center">
                        <img src={logoSrc} alt="Company Logo" className="h-16 w-auto object-contain" onError={e => { e.target.style.display = 'none'; }} />
                        <div className="no-print absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1.5">
                          <button onClick={() => setShowLogoUploader(true)} className="p-1.5 bg-emerald-500 hover:bg-slate-700 text-white rounded-lg"><Upload size={14} /></button>
                          {customLogo && <button onClick={() => setCustomLogo(null)} className="p-1.5 bg-slate-500 hover:bg-slate-600 text-white rounded-lg"><RotateCcw size={14} /></button>}
                        </div>
                      </div>
                    ) : (
                      <div onClick={() => setShowLogoUploader(true)} className="no-print h-16 w-28 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center hover:border-slate-300 hover:bg-slate-50 transition-all cursor-pointer">
                        <div className="text-center"><Upload size={20} className="mx-auto text-slate-400 mb-1" /><p className="text-xs text-slate-400">Upload Logo</p></div>
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-slate-600 leading-relaxed">
                    {websiteInfo?.websiteSlogan && <p className="italic font-medium mb-0.5">{websiteInfo.websiteSlogan}</p>}
                    {websiteInfo?.websiteName && <p className="font-semibold mb-0.5">{websiteInfo.websiteName}</p>}
                    {websiteInfo?.websiteContact && <p className="font-medium">{websiteInfo.websiteContact}</p>}
                  </div>
                </div>
                <div className="text-center flex-1">
                  <h2 className="text-2xl font-bold text-slate-800">Spare Parts Item Requests</h2>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-right">
                    <span className="text-xs font-medium text-slate-500 block mb-1">Document Code:</span>
                    <div className="px-3 py-1.5 bg-slate-50 text-slate-600 border border-slate-200 rounded-lg font-mono text-sm font-semibold">SPR-F-04-SP-REQ.F-001</div>
                  </div>
                  <div className="no-print flex gap-1 mt-5">
                    <button onClick={() => exportSinglePDF(selectedMR)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" title="Download PDF"><Download className="h-5 w-5 text-slate-600" /></button>
                    <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors"><X className="h-5 w-5 text-slate-600" /></button>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-8 bg-white flex-1 overflow-y-auto">
              <div className="border-2 border-slate-300 mb-4">
                <div className="grid grid-cols-12 border-b-2 border-slate-300">
                  <div className="col-span-2 border-r-2 border-slate-300 p-3"><p className="text-xs font-bold text-slate-900 mb-1">Date:</p><p className="text-sm text-slate-700">{formatDate(selectedMR.mrDate)}</p></div>
                  <div className="col-span-5 border-r-2 border-slate-300 p-3"><p className="text-xs font-bold text-slate-900 mb-1">Name:</p><p className="text-sm text-slate-700">{selectedMR.requestedByName}</p></div>
                  <div className="col-span-5 p-3"><p className="text-xs font-bold text-slate-900 mb-1">Good Issuance No.:</p><p className="text-sm text-slate-700">{getShiftGINumber(getShift(getMRTimestamp(selectedMR)).key)}</p></div>                </div>
                <div className="grid grid-cols-12 border-b-2 border-slate-300">
                  <div className="col-span-7 border-r-2 border-slate-300 p-3"><p className="text-xs font-bold text-slate-900 mb-1">Department:</p><p className="text-sm text-slate-700">{selectedMR.department || selectedMR.departmentName || 'N/A'}</p></div>
                  <div className="col-span-5 p-3"><p className="text-xs font-bold text-slate-900 mb-1">Control No.:</p><p className="text-sm text-slate-700">{selectedMR.controlNo || selectedMR.mrNo}</p></div>
                </div>
                <div className="p-3 border-b-2 border-slate-300">
                  <div className="grid grid-cols-7 gap-2 text-xs text-slate-700">
                    {DEPT1.map(d => <label key={d} className="flex items-center gap-1"><input type="checkbox" className="w-3 h-3 accent-slate-600" disabled checked={!!(selectedMR.department || selectedMR.departmentName || '').toLowerCase().includes(d.toLowerCase())} /><span>{d}</span></label>)}
                  </div>
                </div>
                <div className="p-3">
                  <div className="grid grid-cols-7 gap-2 text-xs text-slate-700">
                    {DEPT2.map(d => <label key={d} className="flex items-center gap-1"><input type="checkbox" className="w-3 h-3 accent-slate-600" disabled checked={!!(selectedMR.department || selectedMR.departmentName || '').toLowerCase().includes(d.toLowerCase())} /><span>{d}</span></label>)}
                  </div>
                </div>
              </div>
              {selectedMR.remarks && (
                <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4 rounded-r-lg">
                  <p className="text-xs text-amber-700 uppercase font-bold mb-1">REMARKS</p>
                  <p className="text-sm text-slate-700">{selectedMR.remarks}</p>
                </div>
              )}
              <div className="border-2 border-slate-300 overflow-hidden mb-4">
                <table className="w-full">
                  <thead className="bg-slate-100 border-b-2 border-slate-300">
                    <tr>
                      {['Item Code', 'Qty.', 'UOM', 'Item Description', 'Purpose'].map(h => (
                        <th key={h} className="px-4 py-2.5 text-left text-xs font-bold text-slate-800 uppercase border-r-2 border-slate-300 last:border-r-0">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedMR.mrDetails || []).map((item, index) => (
                      <tr key={item.id || index} className="border-b border-slate-200 hover:bg-slate-50/30 transition-colors">
                        <td className="px-4 py-3 border-r-2 border-slate-200 text-sm font-bold text-slate-800">{item.itemCode}</td>
                        <td className="px-4 py-3 text-center border-r-2 border-slate-200 text-sm text-slate-700">{item.requestedQty}</td>
                        <td className="px-4 py-3 text-center border-r-2 border-slate-200 text-sm text-slate-700">{item.uom}</td>
                        <td className="px-4 py-3 border-r-2 border-slate-200 text-sm text-slate-700">{item.itemDescription}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{item.purpose || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="bg-white border-t-2 border-slate-200 p-8 rounded-b-2xl flex-shrink-0">
              <div className="border-2 border-slate-300 p-4 rounded-lg">
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: 'Requested By', name: selectedMR.requestedByName, sig: selectedMR.requestedBySignature, sub: "(Requestor's Signature & Date)" },
                    { label: 'Approved By', name: selectedMR.approvedByName, sig: selectedMR.approvedBySignature, sub: '(Supervisor/Immediate Supervisor) Signature & Date' },
                    { label: 'Released By', name: selectedMR.releasedByName, sig: selectedMR.releasedBySignature, sub: "(Spare Parts Personnel's) Signature & Date", empty: 'Not yet released' },
                  ].map(({ label, name, sig, sub, empty }) => (
                    <div key={label}>
                      <p className="text-xs font-bold text-slate-800 mb-2">{label}:</p>
                      <div className="h-20 flex flex-col items-center justify-end border-b-2 border-slate-800 mb-2 pb-1">
                        {sig && <img src={sig} alt="Signature" className="h-10 object-contain mb-1" />}
                        {name ? <p className="text-sm font-bold text-slate-900 text-center">{name}</p> : empty ? <p className="text-xs text-slate-400 italic text-center">{empty}</p> : null}
                      </div>
                      <p className="text-xs text-slate-500 italic text-center">{sub}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end mt-5">
                <button onClick={() => setShowModal(false)} className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all shadow-sm">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showLogoUploader && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-slate-800">Upload Custom Logo</h3>
              <button onClick={() => setShowLogoUploader(false)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X size={18} className="text-slate-500" /></button>
            </div>
            <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-slate-300 hover:bg-slate-50 transition-all cursor-pointer" onClick={() => document.getElementById('logoInputFolder').click()}>
              <Upload size={40} className="mx-auto text-slate-400 mb-2" />
              <p className="text-slate-600 font-medium text-sm mb-1">Click to upload logo</p>
              <p className="text-xs text-slate-400">PNG, JPG up to 2MB</p>
            </div>
            <input type="file" id="logoInputFolder" accept="image/*" onChange={handleLogoUpload} className="hidden" />
            {customLogo && <button onClick={() => { setCustomLogo(null); setShowLogoUploader(false); }} className="mt-3 w-full py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-sm font-medium transition-colors">Reset to Default</button>}
          </div>
        </div>
      )}
      <style jsx>{`@media print{.no-print{display:none!important;}}`}</style>
    </div>
  );
}